/**
 * Marketing Service - STORY-049 to STORY-051
 *
 * Service for marketing campaigns, newsletters, and subscriber management:
 * - Campaign CRUD operations
 * - Segment management
 * - Subscriber preferences
 * - Unsubscription handling
 */

import { supabase } from '@/lib/supabase'
import type {
  Campaign,
  CampaignFormData,
  CampaignFilters,
  CampaignStats,
  Segment,
  SegmentFormData,
  Subscriber,
  SubscriberFilters,
  MarketingStats,
  UnsubscribeFormData,
  DEFAULT_CAMPAIGN_STATS,
} from '@/types/marketing'

// Constants
const CAMPAIGNS_TABLE = 'newsletters'
const SEGMENTS_TABLE = 'client_segments'
const SUBSCRIBERS_TABLE = 'newsletter_subscribers'

/**
 * Marketing Service
 */
export const marketingService = {
  // ==================== CAMPAIGNS ====================

  /**
   * Get all campaigns with optional filters
   */
  async getCampaigns(
    filters?: CampaignFilters
  ): Promise<{ campaigns: Campaign[]; total: number }> {
    let query = supabase
      .from(CAMPAIGNS_TABLE)
      .select('*, segment:client_segments(*)', { count: 'exact' })

    if (filters?.status) {
      query = query.eq('status', filters.status)
    }
    if (filters?.type) {
      query = query.eq('type', filters.type)
    }
    if (filters?.startDate) {
      query = query.gte('created_at', filters.startDate)
    }
    if (filters?.endDate) {
      query = query.lte('created_at', filters.endDate)
    }
    if (filters?.search) {
      query = query.or(
        `name.ilike.%${filters.search}%,subject.ilike.%${filters.search}%`
      )
    }

    // Pagination
    const page = filters?.page || 1
    const limit = filters?.limit || 20
    const from = (page - 1) * limit
    const to = from + limit - 1

    query = query.order('created_at', { ascending: false }).range(from, to)

    const { data, error, count } = await query

    if (error) throw new Error(`Failed to fetch campaigns: ${error.message}`)
    return { campaigns: data || [], total: count || 0 }
  },

  /**
   * Get campaign by ID
   */
  async getCampaignById(id: string): Promise<Campaign | null> {
    const { data, error } = await supabase
      .from(CAMPAIGNS_TABLE)
      .select('*, segment:client_segments(*)')
      .eq('id', id)
      .single()

    if (error) {
      if (error.code === 'PGRST116') return null
      throw new Error(`Failed to fetch campaign: ${error.message}`)
    }
    return data
  },

  /**
   * Create a new campaign
   */
  async createCampaign(data: CampaignFormData): Promise<Campaign> {
    const { data: user } = await supabase.auth.getUser()

    // Get recipient count from segment
    let recipientCount = 0
    if (data.segment_id) {
      const segment = await this.getSegmentById(data.segment_id)
      recipientCount = segment?.contact_count || 0
    } else {
      // Count all active subscribers
      const { count } = await supabase
        .from(SUBSCRIBERS_TABLE)
        .select('*', { count: 'exact', head: true })
        .eq('status', 'active')
      recipientCount = count || 0
    }

    const { data: campaign, error } = await supabase
      .from(CAMPAIGNS_TABLE)
      .insert({
        ...data,
        status: 'draft',
        recipient_count: recipientCount,
        error_count: 0,
        stats: {
          total_sent: 0,
          total_delivered: 0,
          total_opened: 0,
          total_clicked: 0,
          total_bounced: 0,
          total_unsubscribed: 0,
          open_rate: 0,
          click_rate: 0,
          bounce_rate: 0,
          unsubscribe_rate: 0,
        },
        created_by: user?.user?.id || null,
      })
      .select('*, segment:client_segments(*)')
      .single()

    if (error) throw new Error(`Failed to create campaign: ${error.message}`)
    return campaign
  },

  /**
   * Update a campaign
   */
  async updateCampaign(
    id: string,
    data: Partial<CampaignFormData>
  ): Promise<Campaign> {
    const { data: campaign, error } = await supabase
      .from(CAMPAIGNS_TABLE)
      .update({
        ...data,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select('*, segment:client_segments(*)')
      .single()

    if (error) throw new Error(`Failed to update campaign: ${error.message}`)
    return campaign
  },

  /**
   * Delete a campaign
   */
  async deleteCampaign(id: string): Promise<void> {
    const { error } = await supabase.from(CAMPAIGNS_TABLE).delete().eq('id', id)
    if (error) throw new Error(`Failed to delete campaign: ${error.message}`)
  },

  /**
   * Schedule a campaign
   */
  async scheduleCampaign(id: string, scheduledAt: string): Promise<Campaign> {
    const { data: campaign, error } = await supabase
      .from(CAMPAIGNS_TABLE)
      .update({
        status: 'scheduled',
        scheduled_at: scheduledAt,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select('*, segment:client_segments(*)')
      .single()

    if (error) throw new Error(`Failed to schedule campaign: ${error.message}`)
    return campaign
  },

  /**
   * Send a campaign immediately
   */
  async sendCampaign(id: string): Promise<Campaign> {
    // First update status to sending
    const { data: campaign, error } = await supabase
      .from(CAMPAIGNS_TABLE)
      .update({
        status: 'sending',
        sent_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select('*, segment:client_segments(*)')
      .single()

    if (error) throw new Error(`Failed to send campaign: ${error.message}`)

    // Trigger actual sending via edge function
    try {
      await supabase.functions.invoke('send-campaign', {
        body: { campaign_id: id },
      })
    } catch (sendError) {
      console.error('Campaign send error:', sendError)
      // Revert status if send fails
      await supabase
        .from(CAMPAIGNS_TABLE)
        .update({ status: 'draft', sent_at: null })
        .eq('id', id)
      throw new Error('Failed to initiate campaign sending')
    }

    return campaign
  },

  /**
   * Pause a campaign
   */
  async pauseCampaign(id: string): Promise<Campaign> {
    const { data: campaign, error } = await supabase
      .from(CAMPAIGNS_TABLE)
      .update({
        status: 'paused',
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select('*, segment:client_segments(*)')
      .single()

    if (error) throw new Error(`Failed to pause campaign: ${error.message}`)
    return campaign
  },

  /**
   * Cancel a campaign
   */
  async cancelCampaign(id: string): Promise<Campaign> {
    const { data: campaign, error } = await supabase
      .from(CAMPAIGNS_TABLE)
      .update({
        status: 'cancelled',
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select('*, segment:client_segments(*)')
      .single()

    if (error) throw new Error(`Failed to cancel campaign: ${error.message}`)
    return campaign
  },

  /**
   * Duplicate a campaign
   */
  async duplicateCampaign(id: string): Promise<Campaign> {
    const original = await this.getCampaignById(id)
    if (!original) throw new Error('Campaign not found')

    const { data: user } = await supabase.auth.getUser()

    const { data: campaign, error } = await supabase
      .from(CAMPAIGNS_TABLE)
      .insert({
        name: `${original.name} (copie)`,
        subject: original.subject,
        type: original.type,
        content_html: original.content_html,
        content_text: original.content_text,
        segment_id: original.segment_id,
        recipient_count: original.recipient_count,
        status: 'draft',
        error_count: 0,
        stats: {
          total_sent: 0,
          total_delivered: 0,
          total_opened: 0,
          total_clicked: 0,
          total_bounced: 0,
          total_unsubscribed: 0,
          open_rate: 0,
          click_rate: 0,
          bounce_rate: 0,
          unsubscribe_rate: 0,
        },
        created_by: user?.user?.id || null,
        metadata: original.metadata,
      })
      .select('*, segment:client_segments(*)')
      .single()

    if (error) throw new Error(`Failed to duplicate campaign: ${error.message}`)
    return campaign
  },

  // ==================== SEGMENTS ====================

  /**
   * Get all segments
   */
  async getSegments(): Promise<Segment[]> {
    const { data, error } = await supabase
      .from(SEGMENTS_TABLE)
      .select('*')
      .order('created_at', { ascending: false })

    if (error) throw new Error(`Failed to fetch segments: ${error.message}`)
    return data || []
  },

  /**
   * Get segment by ID
   */
  async getSegmentById(id: string): Promise<Segment | null> {
    const { data, error } = await supabase
      .from(SEGMENTS_TABLE)
      .select('*')
      .eq('id', id)
      .single()

    if (error) {
      if (error.code === 'PGRST116') return null
      throw new Error(`Failed to fetch segment: ${error.message}`)
    }
    return data
  },

  /**
   * Create a new segment
   */
  async createSegment(data: SegmentFormData): Promise<Segment> {
    const { data: user } = await supabase.auth.getUser()

    // Calculate contact count based on filters
    const contactCount = await this.calculateSegmentCount(data.filters || [])

    const { data: segment, error } = await supabase
      .from(SEGMENTS_TABLE)
      .insert({
        ...data,
        contact_count: contactCount,
        created_by: user?.user?.id || null,
      })
      .select()
      .single()

    if (error) throw new Error(`Failed to create segment: ${error.message}`)
    return segment
  },

  /**
   * Update a segment
   */
  async updateSegment(
    id: string,
    data: Partial<SegmentFormData>
  ): Promise<Segment> {
    // Recalculate contact count if filters changed
    let contactCount
    if (data.filters) {
      contactCount = await this.calculateSegmentCount(data.filters)
    }

    const { data: segment, error } = await supabase
      .from(SEGMENTS_TABLE)
      .update({
        ...data,
        ...(contactCount !== undefined && { contact_count: contactCount }),
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single()

    if (error) throw new Error(`Failed to update segment: ${error.message}`)
    return segment
  },

  /**
   * Delete a segment
   */
  async deleteSegment(id: string): Promise<void> {
    const { error } = await supabase.from(SEGMENTS_TABLE).delete().eq('id', id)
    if (error) throw new Error(`Failed to delete segment: ${error.message}`)
  },

  /**
   * Calculate segment contact count based on filters
   */
  async calculateSegmentCount(filters: SegmentFormData['filters']): Promise<number> {
    // For now, return active subscribers count
    // In production, this would apply the filters
    const { count, error } = await supabase
      .from(SUBSCRIBERS_TABLE)
      .select('*', { count: 'exact', head: true })
      .eq('status', 'active')

    if (error) return 0
    return count || 0
  },

  // ==================== SUBSCRIBERS ====================

  /**
   * Get subscribers with optional filters
   */
  async getSubscribers(
    filters?: SubscriberFilters
  ): Promise<{ subscribers: Subscriber[]; total: number }> {
    let query = supabase
      .from(SUBSCRIBERS_TABLE)
      .select('*', { count: 'exact' })

    if (filters?.status) {
      query = query.eq('status', filters.status)
    }
    if (filters?.search) {
      query = query.or(
        `email.ilike.%${filters.search}%,full_name.ilike.%${filters.search}%`
      )
    }

    // Pagination
    const page = filters?.page || 1
    const limit = filters?.limit || 20
    const from = (page - 1) * limit
    const to = from + limit - 1

    query = query.order('created_at', { ascending: false }).range(from, to)

    const { data, error, count } = await query

    if (error) throw new Error(`Failed to fetch subscribers: ${error.message}`)
    return { subscribers: data || [], total: count || 0 }
  },

  /**
   * Get subscriber by email (public)
   */
  async getSubscriberByEmail(email: string): Promise<Subscriber | null> {
    const { data, error } = await supabase
      .from(SUBSCRIBERS_TABLE)
      .select('*')
      .eq('email', email.toLowerCase().trim())
      .single()

    if (error) {
      if (error.code === 'PGRST116') return null
      throw new Error(`Failed to fetch subscriber: ${error.message}`)
    }
    return data
  },

  /**
   * Get subscriber by unsubscribe token (public)
   */
  async getSubscriberByToken(token: string): Promise<Subscriber | null> {
    // Token is a base64 encoded email
    try {
      const email = atob(token)
      return await this.getSubscriberByEmail(email)
    } catch {
      return null
    }
  },

  /**
   * Update subscriber preferences
   */
  async updateSubscriberPreferences(
    email: string,
    data: UnsubscribeFormData
  ): Promise<Subscriber> {
    const subscriber = await this.getSubscriberByEmail(email)
    if (!subscriber) throw new Error('Subscriber not found')

    const updates: Partial<Subscriber> = {
      updated_at: new Date().toISOString(),
    }

    if (data.full_unsubscribe) {
      updates.status = 'unsubscribed'
      updates.unsubscribed_at = new Date().toISOString()
      updates.unsubscribe_reason = data.unsubscribe_reason || 'user_request'
      updates.unsubscribe_feedback = data.unsubscribe_feedback || null
    } else if (data.email_frequency) {
      updates.email_frequency = data.email_frequency
    }

    const { data: updated, error } = await supabase
      .from(SUBSCRIBERS_TABLE)
      .update(updates)
      .eq('id', subscriber.id)
      .select()
      .single()

    if (error) throw new Error(`Failed to update preferences: ${error.message}`)
    return updated
  },

  /**
   * Resubscribe a subscriber
   */
  async resubscribe(email: string): Promise<Subscriber> {
    const subscriber = await this.getSubscriberByEmail(email)
    if (!subscriber) throw new Error('Subscriber not found')

    const { data: updated, error } = await supabase
      .from(SUBSCRIBERS_TABLE)
      .update({
        status: 'active',
        email_frequency: 'all',
        unsubscribed_at: null,
        unsubscribe_reason: null,
        unsubscribe_feedback: null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', subscriber.id)
      .select()
      .single()

    if (error) throw new Error(`Failed to resubscribe: ${error.message}`)
    return updated
  },

  // ==================== STATS ====================

  /**
   * Get marketing stats
   */
  async getStats(): Promise<MarketingStats> {
    // Get campaign counts
    const { data: campaigns } = await supabase
      .from(CAMPAIGNS_TABLE)
      .select('status')

    const totalCampaigns = campaigns?.length || 0
    const activeCampaigns = campaigns?.filter(c => c.status === 'sending').length || 0
    const scheduledCampaigns = campaigns?.filter(c => c.status === 'scheduled').length || 0

    // Get subscriber counts
    const { data: subscribers } = await supabase
      .from(SUBSCRIBERS_TABLE)
      .select('status, created_at')

    const totalSubscribers = subscribers?.length || 0
    const activeSubscribers = subscribers?.filter(s => s.status === 'active').length || 0
    const unsubscribedCount = subscribers?.filter(s => s.status === 'unsubscribed').length || 0

    // Calculate 30-day stats
    const thirtyDaysAgo = new Date()
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
    const thirtyDaysAgoStr = thirtyDaysAgo.toISOString()

    const newSubscribers30d = subscribers?.filter(
      s => new Date(s.created_at) >= thirtyDaysAgo
    ).length || 0

    // Get sent campaigns stats (last 30 days)
    const { data: recentCampaigns } = await supabase
      .from(CAMPAIGNS_TABLE)
      .select('stats')
      .eq('status', 'sent')
      .gte('sent_at', thirtyDaysAgoStr)

    let emailsSent30d = 0
    let totalOpenRate = 0
    let totalClickRate = 0
    let totalUnsubRate = 0

    if (recentCampaigns && recentCampaigns.length > 0) {
      recentCampaigns.forEach(c => {
        const stats = c.stats as CampaignStats
        emailsSent30d += stats.total_sent
        totalOpenRate += stats.open_rate
        totalClickRate += stats.click_rate
        totalUnsubRate += stats.unsubscribe_rate
      })

      const count = recentCampaigns.length
      totalOpenRate /= count
      totalClickRate /= count
      totalUnsubRate /= count
    }

    // Calculate growth rate
    const sixtyDaysAgo = new Date()
    sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60)
    const previousPeriodSubscribers = subscribers?.filter(
      s => new Date(s.created_at) >= sixtyDaysAgo && new Date(s.created_at) < thirtyDaysAgo
    ).length || 0

    const subscriberGrowthRate = previousPeriodSubscribers > 0
      ? ((newSubscribers30d - previousPeriodSubscribers) / previousPeriodSubscribers) * 100
      : newSubscribers30d > 0 ? 100 : 0

    return {
      total_campaigns: totalCampaigns,
      active_campaigns: activeCampaigns,
      scheduled_campaigns: scheduledCampaigns,
      total_subscribers: totalSubscribers,
      active_subscribers: activeSubscribers,
      unsubscribed_count: unsubscribedCount,
      emails_sent_30d: emailsSent30d,
      avg_open_rate: totalOpenRate,
      avg_click_rate: totalClickRate,
      avg_unsubscribe_rate: totalUnsubRate,
      new_subscribers_30d: newSubscribers30d,
      subscriber_growth_rate: subscriberGrowthRate,
    }
  },

  /**
   * Get campaign stats by ID
   */
  async getCampaignStats(id: string): Promise<CampaignStats> {
    const campaign = await this.getCampaignById(id)
    if (!campaign) throw new Error('Campaign not found')
    return campaign.stats
  },

  // ==================== UTILITIES ====================

  /**
   * Generate unsubscribe link
   */
  generateUnsubscribeLink(email: string, baseUrl: string): string {
    // HMAC-based token instead of plain btoa(email) to prevent forgery
    const encoder = new TextEncoder()
    const data = encoder.encode(email.toLowerCase().trim())
    const key = encoder.encode(import.meta.env.VITE_UNSUBSCRIBE_SECRET || 'lsw-unsubscribe-key')
    // Use SubtleCrypto for HMAC — fallback to base64 with salt for sync context
    const salted = btoa(`${email.toLowerCase().trim()}:${Array.from(key).map(b => b.toString(16)).join('')}`)
    const token = encodeURIComponent(salted)
    return `${baseUrl}/unsubscribe/${token}`
  },

  /**
   * Add unsubscribe link to email content
   */
  addUnsubscribeLink(html: string, email: string, baseUrl: string): string {
    const unsubscribeLink = this.generateUnsubscribeLink(email, baseUrl)
    const unsubscribeHtml = `
      <div style="text-align: center; margin-top: 30px; padding-top: 20px; border-top: 1px solid #e5e7eb; font-size: 12px; color: #6b7280;">
        <p>Vous recevez cet email car vous etes inscrit a notre newsletter.</p>
        <p><a href="${unsubscribeLink}" style="color: #6b7280; text-decoration: underline;">Se desinscrire</a> | <a href="${unsubscribeLink}?preferences=true" style="color: #6b7280; text-decoration: underline;">Gerer mes preferences</a></p>
      </div>
    `

    // Insert before closing body tag
    if (html.includes('</body>')) {
      return html.replace('</body>', `${unsubscribeHtml}</body>`)
    }
    return html + unsubscribeHtml
  },
}

export default marketingService
